import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useInvoiceStore, useUserStore } from "../../../state";
import { useInvoiceSearch } from "../../../hooks";
import ExpenseEmptyIcon from "../../../assets/icons/InvCusExpEmptyIcon";
import EmptyPage from "../../../components/emptyPage/EmptyPage";
import { useInvoiceStatsData } from "../../../hooks/Stats/invoice";
import { servicesType } from "../../../constants/Subscription";
import ActionModal from "../../../components/common/ActionModal";
import UploadIcon from "../../../assets/icons/svgicons/UploadIcon.svg";
import CreateInvoiceIcon from "../../../assets/icons/svgicons/createInvoice.svg";
import { SubscriptionModal } from "../../../components/common/Subscription/SubscriptionModal";
import { MobileSubscriptionModal } from "../../../components/common/Subscription/MobileSubscriptionModal";
import { defaultShortcuts } from "../../../constants/helpServices";
import DatePickerWithShortcuts from "../../../components/DatePicker";
import { ICustomer, IInvoice } from "../../../@types";
import { useTranslation } from "react-i18next";
import useRetailBusinessStatus from "../../../hooks/invoice/useRetailBusinessStatus";
import generateActionButtons from "../../../components/Sales/invoice/invoiceCreationModal";
import { ListInfoCardProps } from "../../../components/list-page/desktop/ListInfoCard";
import ReusableListPage from "../../../components/list-page";
import { columns, renderInvoicesData } from "../../../utils/invoice-list";
import {
  invoiceCategories,
  mobileInvoiceCategories,
} from "../../../constants/filterCategories";
import PageLoader from "../../../components/common/PageLoader";
import dayjs from "dayjs";
import { useListInvoiceTourGuide } from "../../../components/tourGide/useListInvoiceTourGuide";
import { useTour } from "../../../context/TourContext";
import { useIsMobile } from "../../../components/tourGide/useIsMobile";
import { safeToISOString } from "../../../utils/date";
import { useCompanyStore } from "../../../state/useCompanyStore";
import { formatDate } from "../../../utils";

const ListInvoicePage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { registerSteps, startTour } = useTour();
  const { company: user, listInvoice } = useCompanyStore();
  const isMobile = useIsMobile();
  const { listInvoiceTourGuide } = useListInvoiceTourGuide();

  registerSteps("listInvoice", [...listInvoiceTourGuide]);

  React.useEffect(() => {
    if (
      user?.prefs?.isFirstInvoice &&
      listInvoice &&
      !isMobile &&
      !startDateParam &&
      !!user.businessType
    ) {
      startTour("listInvoice");
    }
  }, [registerSteps]);

  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");
  const selectedParam = searchParams.get("selectedLabel");

  const navigate = useNavigate();
  const currentDate = new Date();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: safeToISOString(startDateParam),
    endDate: safeToISOString(endDateParam),
  });
  const [startOfMonth, setStartOfMonth] = useState(
    startDateParam
      ? new Date(startDateParam)
      : dayjs().subtract(30, "days").startOf("day").toDate(),
  );
  const [endOfMonth, setEndOfMonth] = useState(
    endDateParam ? new Date(endDateParam) : dayjs().endOf("day").toDate(),
  );
  const { invoiceData, error } = useInvoiceStatsData(startOfMonth, endOfMonth);
  const fetchMoreData = useInvoiceStore((state) => state.fetchMoreData);
  const hasData = useInvoiceStore((state) => state.hasData);
  const onSearchData = useInvoiceStore((state) => state.setSearchText);
  const handleFilter = useInvoiceStore((state) => state.setFilter);
  const fetchData = useInvoiceStore((state) => state.fetchData);
  const clearAllFilters = useInvoiceStore((state) => state.clearAllFilters);
  const { t } = useTranslation("invoices");
  const invoices = useInvoiceStore((state) => state.invoices);
  const isLoading = useInvoiceStore((state) => state.isLoading);
  const isLoadingInitial = useInvoiceStore((state) => state.isLoadingInitial);
  const isLoadingFilter = useInvoiceStore((state) => state.isLoadingFilter);
  const isLoadingMore = useInvoiceStore((state) => state.isLoadingMore);
  const range = useInvoiceStore((state) => state.range);
  const pagination = useInvoiceStore((state) => state.pagination);
  const searchText = useInvoiceStore((state) => state.searchText);
  const filter = useInvoiceStore((state) => state.filter);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    // Check if the query parameter is present
    const openModal = searchParams.get("openModal") === "true";
    setIsModalOpen(openModal);
  }, [searchParams]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const [showSubscriptionModal, setShowSubscriptionModal] =
    React.useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const isRetailBusiness = useRetailBusinessStatus();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>("Last 30 Days");
  const [selected, setSelected] = useState<[Date, Date] | null>(null);
  const {
    data: searchResult,
    isSearching,
    searchedDone,
    setSearchedDone,
    handleSearch: handleInvoiceSearch,
  } = useInvoiceSearch();

  const clearAllFilter = () => {
    setDateRange({ startDate: "", endDate: "" });
    setStartDate(null);
    setEndDate(null);
    onSearchData("");
    handleFilter({ status: "", isReoccurring: null });
    setSelectedLabel("Last 30 Days");
    setStartOfMonth(dayjs().subtract(30, "days").startOf("day").toDate());
    setEndOfMonth(dayjs().endOf("day").toDate());
    fetchData();
  };

  useEffect(() => {
    // @ts-ignore
    if (filter && dateRange.startDate == "" && dateRange.endDate == "") {
      fetchData({ actionType: "filter" });
    }
  }, [searchText, filter]);

  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (startDateParam && endDateParam && !isFetching) {
      setSelectedLabel(selectedParam || "Last 30 Days");
      setStartOfMonth(new Date(startDateParam));
      setEndOfMonth(new Date(endDateParam));
      fetchData({
        startDate: new Date(startDateParam)?.toISOString().replace(".000Z", ""),
        endDate: new Date(endDateParam)?.toISOString().replace(".000Z", ""),
      })?.finally(() => {
        navigate("/invoices", { replace: true });
      });
    } else if (dateRange.startDate || dateRange.endDate) {
      fetchData({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    }
  }, [range, dateRange, searchParams]);

  const fetchInvoicePage = () => {
    setIsModalOpen(true);
    return;
  };

  const handleInventoryInvoiceClick = async () => {
    navigate("/invoices/create");
    return;
  };
  const handleRecurringInvoiceClick = async () => {
    navigate("/invoices/recurringList");
  };

  const handleFreeformInvoiceClick = () => {
    navigate("/invoices/createFreeform");
  };

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };
  const infoCardData = [
    {
      title: t("totalInvoices"),
      mobileTitle: t("totalInvoices"),
      value: `${invoiceData?.totalInvoices?.value || 0}`,
      percentageChange: `${invoiceData?.totalInvoices?.percentageChange || 0}`,
      bgColor: "#F0F7FF",
      className: "w-full",
      previousDateRange: `${formatDate(new Date(invoiceData?.prevStartDate!).toDateString())} - ${formatDate(new Date(invoiceData?.prevEndDate!).toDateString())}`,
      dateRange:
        selectedLabel ||
        `${startOfMonth.toDateString()} - ${endOfMonth.toDateString()}`,
    },
    {
      title: t("paidStatus"),
      mobileTitle: t("paidStatus"),
      value: `${invoiceData?.totalPaidInvoices?.value || 0}`,
      percentageChange: `${invoiceData?.totalPaidInvoices?.percentageChange || 0}`,
      bgColor: "#ECFDF3",
      className: "w-full",
      previousDateRange: `${formatDate(new Date(invoiceData?.prevStartDate!).toDateString())} - ${formatDate(new Date(invoiceData?.prevEndDate!).toDateString())}`,
      dateRange:
        selectedLabel ||
        `${startOfMonth.toDateString()} - ${endOfMonth.toDateString()}`,
    },
    {
      title: t("pendingStatus"),
      mobileTitle: t("pendingStatus"),
      value: `${invoiceData?.totalPendingInvoices?.value || 0}`,
      percentageChange: `${invoiceData?.totalPendingInvoices?.percentageChange || 0}`,
      bgColor: "#FFFAEB",
      className: "w-full",
      previousDateRange: `${formatDate(new Date(invoiceData?.prevStartDate!).toDateString())} - ${formatDate(new Date(invoiceData?.prevEndDate!).toDateString())}`,
      dateRange:
        selectedLabel ||
        `${startOfMonth.toDateString()} - ${endOfMonth.toDateString()}`,
    },
    {
      title: t("overdueStatus"),
      mobileTitle: t("overdueStatus"),
      value: `${invoiceData?.totalOverDueInvoices?.value || 0}`,
      percentageChange: `${invoiceData?.totalOverDueInvoices?.percentageChange || 0}`,
      bgColor: "#FEF3F2",
      className: "w-full",
      previousDateRange: `${formatDate(new Date(invoiceData?.prevStartDate!).toDateString())} - ${formatDate(new Date(invoiceData?.prevEndDate!).toDateString())}`,
      dateRange:
        selectedLabel ||
        `${startOfMonth.toDateString()} - ${endOfMonth.toDateString()}`,
    },
  ] as ListInfoCardProps[];

  const handleRangeSelect = async (ranges: any) => {
    setShowCalendar(false);
    setSelected(ranges);
    const startDate = ranges[0];
    const endDate = ranges[1];
    let label = "";
    for (const shortcut of defaultShortcuts) {
      const [shortcutStart, shortcutEnd] = shortcut.range();

      if (
        startDate.getTime() === shortcutStart.getTime() &&
        endDate.getTime() === shortcutEnd.getTime()
      ) {
        label = shortcut.label;
        break;
      }
    }
    if (label) {
      setSelectedLabel(label);
    } else {
      setSelectedLabel(
        `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
      );
    }
    setStartOfMonth(new Date(startDate));
    setEndOfMonth(new Date(endDate));
  };

  const handleApplyButtonClick = async (
    startDate: Date,
    endDate: Date | null,
    toggle = true,
  ) => {
    if (endDate === null) {
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
    }
    setEndDate(startDate);
    setStartDate(endDate);
    const newStartDate = startDate?.toISOString().replace(".000Z", "");
    const newEndDate = endDate?.toISOString().replace(".000Z", "");
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    try {
      await handleRangeSelect([startDate, endDate]);
      toggle && handleCalendarToggle();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (!showCalendar) {
      setSelected(null);
    }
  }, [showCalendar]);

  const handleFilterSubmit = (filter: Record<string, string | boolean>) => {
    if (filter.status !== undefined || filter.isReoccurring !== undefined) {
      //@ts-ignore
      const newFilter: Partial<Filter> = {};
      if (filter.status !== undefined) {
        newFilter.status = filter.status as string;
      }
      if (filter.isReoccurring !== undefined) {
        newFilter.isReoccurring = filter.isReoccurring as boolean | null;
      }
      //@ts-ignore
      handleFilter(newFilter as Filter);
    }
  };

  const actionButtons = generateActionButtons(
    isRetailBusiness,
    handleInventoryInvoiceClick,
    handleFreeformInvoiceClick,
  );

  if (isLoadingInitial) {
    return (
      <>
        <PageLoader />
      </>
    );
  }

  if (error) {
    return (
      <div>
        {typeof error === "object" && "message" in error && error.message
          ? error.message
          : JSON.stringify(error)}
      </div>
    );
  }

  // const [filteredData, setFilteredData] = useState<IInvoice[]>([]);
  const filterDataByCustomerName = <
    T extends { customer: { firstName: string; lastName: string } },
  >(
    data: T[],
    searchTerm: string,
  ): T[] => {
    return data.filter((item) => {
      const fullName =
        `${item.customer.firstName} ${item.customer.lastName}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
  };

  const handleSearch = (searchTerm: string) => {
    const filtered = filterDataByCustomerName(invoices, searchTerm);
    // setFilteredData(filtered);
  };

  return (
    <>
      <div>
        <ReusableListPage
          categories={invoiceCategories}
          mobileCategories={mobileInvoiceCategories}
          createTitle="Create Invoice"
          pageTitle="Invoice"
          showReOccurring={true}
          reOccurringText="Recurring invoice"
          showCalendar={showCalendar}
          infoCardData={infoCardData}
          handleCalendarToggle={handleCalendarToggle}
          handleCreate={fetchInvoicePage}
          selectedLabel={selectedLabel}
          fetchMoreData={fetchMoreData}
          hasData={hasData}
          onSearchData={onSearchData}
          handleFilter={handleFilter}
          handleSearch={handleInvoiceSearch}
          searchText={searchText}
          // onTableSearch={handleSearch}
          meta={pagination}
          data={renderInvoicesData(invoices, null)}
          modules="invoices"
          searchDisplayKey="customer.firstName"
          searchPlaceHolder="Search by invoice number or customer name"
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          isLoadingFilter={isLoadingFilter}
          isSearching={isSearching}
          searchResult={searchResult}
          selectedFilter={filter}
          searchedDone={searchedDone}
          setSearchedDone={setSearchedDone}
          columns={columns}
          onSubmit={handleFilterSubmit}
          mobileCard={"invoiceCard"}
          clearAllFilters={clearAllFilters}
          emptyStateDescription={t("invoice.invoiceFirst")}
          emptyPageIcon={<ExpenseEmptyIcon />}
          emptyPageHref="https://www.youtube.com/embed/fJUWHHoUaxo"
          emptyPageLinkName="invoices"
          handleSetCustomer={function (
            selectedCustomer: ICustomer | null,
          ): void {
            throw new Error("Function not implemented.");
          }}
          emptyMessage={t("invoice.emptyState")}
        />
      </div>

      <div ref={containerRef}>
        <ActionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title="Select an invoice creation option"
          icon={CreateInvoiceIcon}
          actionButtons={actionButtons}
        />
      </div>

      <div className="mt-[-30em]">
        {showSubscriptionModal && (
          <SubscriptionModal>
            <MobileSubscriptionModal
              onClose={() => setShowSubscriptionModal(false)}
              titleText={`Need to access more features?`}
              subtitleText={subscriptionErrorMessage}
            />
          </SubscriptionModal>
        )}
      </div>

      {showCalendar && (
        <>
          <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
            <div className="justify-center items-center flex h-full w-full">
              <DatePickerWithShortcuts
                onRangeSelect={handleRangeSelect}
                selected={selected}
                onClose={handleCalendarToggle}
                onApplyButtonClick={(startDate, endDate) =>
                  handleApplyButtonClick(startDate, endDate)
                }
                endDate={endDate ?? endOfMonth}
                startDate={startDate ?? startOfMonth}
                fetchData={clearAllFilter}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { ListInvoicePage };
